// ============================================================================
//  roles.js — the client-side mirror of the database's authority rules.
//
//  DISPLAY GATING ONLY. Nothing here secures anything: the RLS policies and
//  the security-definer RPCs in migrations 0004/0005 are the enforcement
//  point. This file exists so the UI does not offer a man a button that the
//  server is going to refuse.
//
//  Keep it table-driven and boring so it can be diffed by eye against the
//  authority matrix in the migrations. If the two ever disagree, the database
//  is right.
// ============================================================================

/** Mirrors role_rank() in 0004. */
export const RANK = {
  mentee: 1,
  mentor: 2,
  admin: 3,
  super_admin: 4,
};

/** What men actually call these. The enum values are historical. */
export const ROLE_LABEL = {
  mentee: "Member",
  mentor: "Team Admin",
  admin: "Admin",
  super_admin: "Super Admin",
};

/** One line each, for the role picker. */
export const ROLE_BLURB = {
  mentee: "Tracks his own goals.",
  mentor: "Runs one team: invites, suspends, renames.",
  admin: "Runs several teams and can create new ones.",
  super_admin: "Everything, everywhere.",
};

export const DEFAULT_TEAM_CAPACITY = 15;

export const rank = (role) => RANK[role] ?? 0;
export const label = (role) => ROLE_LABEL[role] ?? "Member";

/** A suspended man has no powers at all, matching my_rank()'s deleted_at check. */
export function effectiveRank(person) {
  if (!person || person.is_suspended || person.deleted_at) return 0;
  return rank(person.role);
}

/** Does this man lead that team? Mirrors led_group_ids(). */
export function leadsTeam(me, ledGroupIds, groupId) {
  if (!me || !groupId) return false;
  if (me.role === "super_admin" && !me.is_suspended) return true;
  return (ledGroupIds ?? []).includes(groupId);
}

/**
 * Mirrors can_manage_man(): strictly lower rank, and on a team I lead.
 * `me` is the viewer's profile, `target` a person row from st.people.
 */
export function canManage(me, target, ledGroupIds) {
  if (!me || !target) return false;
  if (me.id === target.id) return false;          // never yourself
  const myRank = effectiveRank(me);
  if (myRank < 2) return false;
  if (rank(target.role) >= myRank) return false;  // equal or higher is off limits
  if (me.role === "super_admin") return true;
  return (ledGroupIds ?? []).includes(target.group_id);
}

/** Roles this man may grant — strictly below his own. */
export function grantableRoles(me) {
  const myRank = effectiveRank(me);
  return Object.keys(RANK).filter((r) => RANK[r] < myRank);
}

/** Admin and above can create teams. Mirrors create_team's my_rank() >= 3. */
export function canCreateTeams(me) {
  return effectiveRank(me) >= 3;
}

/** Can he rename / set capacity on this team? */
export function canEditTeam(me, ledGroupIds, groupId) {
  return effectiveRank(me) >= 2 && leadsTeam(me, ledGroupIds, groupId);
}

/**
 * Headcount for a team, counting leaders — a Team Admin is a man in the
 * cohort. Matches team_member_count() in 0004.
 */
export function teamCount(people, groupId) {
  return Object.values(people ?? {})
    .filter((p) => p.group_id === groupId && !p.is_suspended).length;
}

export function teamCapacity(groups, groupId) {
  return groups?.[groupId]?.max_members ?? DEFAULT_TEAM_CAPACITY;
}

/** { used, capacity, remaining, full } for a team header or an invite form. */
export function capacityOf(people, groups, groupId) {
  const used = teamCount(people, groupId);
  const capacity = teamCapacity(groups, groupId);
  return { used, capacity, remaining: Math.max(0, capacity - used), full: used >= capacity };
}
